<template>
  <div class="actual-calculation-picker">
    <button
      type="button"
      class="actual-calculation-picker__trigger"
      :class="{
        'actual-calculation-picker__trigger--open': isOpen,
        'actual-calculation-picker__trigger--disabled': disabled,
      }"
      :disabled="disabled"
      :aria-disabled="disabled ? 'true' : 'false'"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="String(isOpen)"
      :aria-controls="listboxId"
      :aria-activedescendant="isOpen ? optionId(activeIndex) : undefined"
      :aria-label="ariaLabel"
      @click="toggleDropdown"
      @blur="closeDropdown"
      @keydown="onKeydown"
      @keyup.space.prevent
    >
      <ActualCalculation :type="selectedOption.value" :label="true" />
      <span
        class="actual-calculation-picker__chevron"
        :class="{ 'actual-calculation-picker__chevron--rotated': isOpen }"
        aria-hidden="true"
      >
        <Icon name="chevron_down" />
      </span>
    </button>

    <div
      v-if="isOpen"
      :id="listboxId"
      class="actual-calculation-picker__menu"
      role="listbox"
      :aria-label="ariaLabel"
    >
      <div
        v-for="(option, index) in options"
        :id="optionId(index)"
        :key="option.value"
        class="actual-calculation-picker__item"
        :class="{
          'actual-calculation-picker__item--selected': selectedValue === option.value,
          'actual-calculation-picker__item--active': activeIndex === index,
        }"
        role="option"
        :aria-selected="String(selectedValue === option.value)"
        @mousedown.prevent
        @click="selectOption(option)"
      >
        <ActualCalculation :type="option.value" :label="true" />
        <span
          v-if="selectedValue === option.value"
          class="actual-calculation-picker__check"
          aria-hidden="true"
        >
          <Icon name="actual_calculation_check_icon" />
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import ActualCalculation from "@components/ActualCalculation/ActualCalculation.vue";
import Icon from "@components/icons/Icon.vue";
import { ref, computed, watch, getCurrentInstance } from "vue";

const OPTIONS = [{ value: "Last" }, { value: "Average" }];

const props = defineProps({
  modelValue: {
    type: String,
    default: "Last",
  },
  ariaLabel: {
    type: String,
    default: "Actual calculation in period",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const options = OPTIONS;
const uid = getCurrentInstance().proxy._uid;
const listboxId = `actual-calculation-picker-${uid}-listbox`;
const optionId = (index) => `actual-calculation-picker-${uid}-option-${index}`;

const isOpen = ref(false);
const selectedValue = ref(props.modelValue);
const activeIndex = ref(0);

watch(
  () => props.modelValue,
  (value) => {
    selectedValue.value = value;
  }
);

const selectedIndex = computed(() =>
  Math.max(
    options.findIndex((option) => option.value === selectedValue.value),
    0
  )
);

const selectedOption = computed(() => options[selectedIndex.value]);

const openDropdown = () => {
  if (props.disabled) return;
  activeIndex.value = selectedIndex.value;
  isOpen.value = true;
};

const closeDropdown = () => {
  isOpen.value = false;
};

const toggleDropdown = () => {
  if (isOpen.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
};

const selectOption = (option) => {
  selectedValue.value = option.value;
  emit("update:modelValue", option.value);
  closeDropdown();
};

const moveActive = (step) => {
  activeIndex.value = (activeIndex.value + step + options.length) % options.length;
};

const onKeydown = (event) => {
  switch (event.key) {
    case "ArrowDown":
    case "ArrowUp":
      event.preventDefault();
      if (isOpen.value) {
        moveActive(event.key === "ArrowDown" ? 1 : -1);
      } else {
        openDropdown();
      }
      break;
    case "Home":
    case "End":
      if (isOpen.value) {
        event.preventDefault();
        activeIndex.value = event.key === "Home" ? 0 : options.length - 1;
      }
      break;
    case "Enter":
    case " ":
      event.preventDefault();
      if (isOpen.value) {
        selectOption(options[activeIndex.value]);
      } else {
        openDropdown();
      }
      break;
    case "Escape":
      if (isOpen.value) {
        event.preventDefault();
        closeDropdown();
      }
      break;
    default:
      break;
  }
};
</script>

<style scoped>
.actual-calculation-picker {
  position: relative;
  width: 184px;
  max-width: 100%;
}

.actual-calculation-picker__trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  border-radius: var(--radius-md, 8px);
  border: 1px solid var(--Colors-Border-border-primary, #d0d5dd);
  background: var(--Colors-Background-bg-primary, #fff);
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  cursor: pointer;
  text-align: left;
}

.actual-calculation-picker__trigger:hover {
  border-color: #98a2b3;
}

.actual-calculation-picker__trigger:focus-visible,
.actual-calculation-picker__trigger--open {
  outline: none;
  border-color: #d6bbfb;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05), 0 0 0 4px rgba(158, 119, 237, 0.24);
}

.actual-calculation-picker__trigger--disabled,
.actual-calculation-picker__trigger--disabled:hover {
  background: #f9fafb;
  border-color: var(--Colors-Border-border-primary, #d0d5dd);
  box-shadow: none;
  cursor: not-allowed;
}

.actual-calculation-picker__chevron {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.actual-calculation-picker__chevron--rotated {
  transform: rotate(180deg);
}

.actual-calculation-picker__menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 50;
  padding: 4px 0;
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03);
}

.actual-calculation-picker__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0 4px;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
}

.actual-calculation-picker__item:hover,
.actual-calculation-picker__item--selected {
  background: #f9fafb;
}

.actual-calculation-picker__item--active {
  box-shadow: inset 0 0 0 2px #7f56d9;
}

.actual-calculation-picker__check {
  display: flex;
  align-items: center;
}
</style>
